/**
 * Transformers for aggregation API responses.
 *
 * These functions convert backend aggregation responses into formats
 * suitable for MetricsBar (overall totals) and currency display.
 *
 * Note: Per-stage metrics (DealPipelineView) are now derived from
 * stage rollup properties via useStages, not from aggregation API.
 */

import type { QuantityValue } from '@/types/crm';
import { formatCurrency, getCachedDecimalFormatter } from '@/utils/currency';

import type { AggregateMetricsResponse } from '../hooks/useAggregateMetrics';

type QuantityAggregation = QuantityValue | QuantityValue[];

/**
 * Numeric value with unit (for display).
 */
export interface NumericValue {
  amount: number;
  unit: string | null;
}

/**
 * Aggregated metrics for MetricsBar display.
 */
export interface MetricsBarData {
  totalDeals: number;
  totalValue: NumericValue[];
  weightedValue: NumericValue[];
}

/**
 * Sum quantity values by currency unit.
 */
export function sumQuantityValues(
  values: Array<QuantityAggregation>
): NumericValue[] {
  const sums: Map<string | null, number> = new Map();

  for (const valueOrArray of values) {
    const entries = Array.isArray(valueOrArray) ? valueOrArray : [valueOrArray];
    for (const value of entries) {
      const { unit } = value;
      const numAmount =
        typeof value.amount === 'string'
          ? parseFloat(value.amount)
          : value.amount;
      const amount = Number.isFinite(numAmount ?? NaN) ? Number(numAmount) : 0;
      sums.set(unit, (sums.get(unit) || 0) + amount);
    }
  }

  const result: NumericValue[] = [];
  for (const [unit, amount] of sums.entries()) {
    result.push({ amount, unit });
  }

  // Sort by unit for consistent ordering (null last)
  result.sort((a, b) => {
    if (a.unit === null) {
      return 1;
    }
    if (b.unit === null) {
      return -1;
    }
    return a.unit.localeCompare(b.unit);
  });

  return result;
}

/**
 * Transform aggregation response into MetricsBar data.
 *
 * Sums all aggregations across all stages to get overall totals.
 *
 * @param response - Aggregation API response
 * @returns MetricsBar data with totals
 *
 * @example
 * ```ts
 * const metricsData = transformForMetricsBar(aggregationResponse);
 * // { totalDeals: 6, totalValue: [{ amount: 125000, unit: 'USD' }], ... }
 * ```
 */
export function transformForMetricsBar(
  response: AggregateMetricsResponse
): MetricsBarData {
  if (!response.results || response.results.length === 0) {
    return {
      totalDeals: 0,
      totalValue: [],
      weightedValue: [],
    };
  }

  let totalDeals = 0;
  const allTotalAmounts: Array<QuantityAggregation> = [];
  const allWeightedAmounts: Array<QuantityAggregation> = [];

  for (const result of response.results) {
    totalDeals += Math.round(Number(result.aggregations.deal_count) || 0);

    const totalAmount = result.aggregations.total_amount;
    if (totalAmount) {
      if (!Array.isArray(totalAmount) || totalAmount.length > 0) {
        allTotalAmounts.push(totalAmount);
      }
    }

    const weightedAmount = result.aggregations.weighted_amount;
    if (weightedAmount) {
      if (!Array.isArray(weightedAmount) || weightedAmount.length > 0) {
        allWeightedAmounts.push(weightedAmount);
      }
    }
  }

  return {
    totalDeals,
    totalValue: sumQuantityValues(allTotalAmounts),
    weightedValue: sumQuantityValues(allWeightedAmounts),
  };
}

/**
 * Format numeric values as currency string for display.
 *
 * Handles multiple currencies by joining with separator.
 * Filters out zero amounts when displaying multiple currencies,
 * but shows at least one zero if all amounts are zero.
 *
 * @param values - Array of numeric values with units
 * @returns Formatted string (e.g., "$125,000" or "$100,000 | €50,000")
 *
 * @example
 * ```ts
 * formatCurrencyValue([{ amount: 125000, unit: 'USD' }])
 * // "$125,000"
 *
 * formatCurrencyValue([{ amount: 100000, unit: 'USD' }, { amount: 50000, unit: 'EUR' }])
 * // "$100,000 | €50,000"
 *
 * formatCurrencyValue([{ amount: 100000, unit: 'USD' }, { amount: 0, unit: null }])
 * // "$100,000" (zero amount filtered out)
 * ```
 *
 * A `null` unit is formatted as a plain whole number (no currency symbol).
 */
export function formatCurrencyValue(values: NumericValue[]): string {
  if (!values || values.length === 0) {
    return '—';
  }

  // Filter out zero amounts when there are multiple currencies
  const nonZeroValues = values.filter(v => v.amount !== 0);

  // If all values are zero, show zero for the first currency
  const valuesToFormat =
    nonZeroValues.length > 0 ? nonZeroValues : values.slice(0, 1);

  const formatted = valuesToFormat.map(value => {
    if (!value.unit) {
      // No currency unit - just return formatted number
      return getCachedDecimalFormatter(0, 0).format(value.amount);
    }

    return formatCurrency(value.amount, value.unit, { fractionDigits: 0 });
  });

  return formatted.join(' | ');
}
